import { Member, ProcessedMember, ScoringWeights } from '../types';

export function processMembersData(
  members: Member[],
  weights: ScoringWeights = { attendance: 0.3, experience: 0.2, contributionGap: 0.5, minCurrentScore: 80 }
): ProcessedMember[] {
  // Filter qualified members (Revenue Received < TYFCB Given AND Green Score > minCurrentScore)
  const qualifiedMembers = members.filter(member => 
    member.revenueReceived < member.tyfcbGiven &&
    (member.currScore || 0) > weights.minCurrentScore
  );

  if (qualifiedMembers.length === 0) {
    return [];
  }

  // Calculate max values for normalization
  const maxOneOnOne = Math.max(...qualifiedMembers.map(m => m.oneOnOneMeetings));
  const maxVisitors = Math.max(...qualifiedMembers.map(m => m.visitors));
  const maxYears = Math.max(...qualifiedMembers.map(m => m.yearsInBNI));
  const maxRevenueReceived = Math.max(...qualifiedMembers.map(m => m.revenueReceived));

  // Process each qualified member
  const processedMembers: ProcessedMember[] = qualifiedMembers.map(member => {
    const contributionGap = member.tyfcbGiven - member.revenueReceived;
    
    // Calculate individual scores (0-100 scale)
    // Use provided current score if available, otherwise calculate from meetings and visitors
    const attendanceScore = member.currScore && member.currScore > 0 
      ? member.currScore 
      : calculateAttendanceScore(
          member.oneOnOneMeetings,
          member.visitors,
          maxOneOnOne,
          maxVisitors
        );
    
    const experienceScore = calculateExperienceScore(member.yearsInBNI, maxYears);
    
    // Revenue Received Score: Lower revenue received = higher score
    const revenueReceivedScore = calculateRevenueReceivedScore(member.revenueReceived, maxRevenueReceived);
    
    // Calculate final weighted score with priority for lower revenue receivers:
    // 1. Revenue Received (lower = higher score) - 50%
    // 2. Green Score - 30%
    // 3. Years in BNI - 20%
    const finalScore = 
      weights.attendance * attendanceScore +
      weights.experience * experienceScore +
      weights.contributionGap * revenueReceivedScore;

    return {
      ...member,
      contributionGap,
      attendanceScore,
      experienceScore,
      contributionGapScore: revenueReceivedScore, // Renamed for consistency
      finalScore,
      isQualified: true,
    };
  });

  // Sort by final score descending
  return processedMembers.sort((a, b) => b.finalScore - a.finalScore);
}

function calculateAttendanceScore(
  oneOnOneMeetings: number,
  visitors: number,
  maxOneOnOne: number,
  maxVisitors: number
): number {
  const oneOnOneScore = maxOneOnOne > 0 ? (oneOnOneMeetings / maxOneOnOne) * 100 : 0;
  const visitorsScore = maxVisitors > 0 ? (visitors / maxVisitors) * 100 : 0;
  
  // Weight: 70% one-on-one meetings, 30% visitors
  return 0.7 * oneOnOneScore + 0.3 * visitorsScore;
}

function calculateExperienceScore(yearsInBNI: number, maxYears: number): number {
  return maxYears > 0 ? (yearsInBNI / maxYears) * 100 : 0;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function calculateContributionGapScore(contributionGap: number, maxContributionGap: number): number {
  return maxContributionGap > 0 ? (contributionGap / maxContributionGap) * 100 : 0;
}

function calculateRevenueReceivedScore(revenueReceived: number, maxRevenueReceived: number): number {
  // Lower revenue received = higher score (inverted calculation)
  return maxRevenueReceived > 0 ? ((maxRevenueReceived - revenueReceived) / maxRevenueReceived) * 100 : 100;
}

export function parseExcelData(data: any[][]): Member[] {
  if (!data || data.length < 2) {
    throw new Error('Invalid data format');
  }

  // const headers = data[0].map((header: any) => 
  //   String(header).toLowerCase().replace(/\s+/g, '')
  // );
  
  const members: Member[] = [];
  
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (row.length === 0 || !row[0]) continue; // Skip empty rows
    
    try {
      const member: Member = {
        sNo: parseInt(row[0]) || i,
        memberName: String(row[1] || '').trim(),
        yearsInBNI: parseFloat(row[2]) || 0,
        oneOnOneMeetings: parseInt(row[3]) || 0,
        refGiven: parseInt(row[4]) || 0,
        refReceived: parseInt(row[5]) || 0,
        tyfcbGiven: parseFloat(row[6]) || 0,
        revenueReceived: parseFloat(row[7]) || 0,
        visitors: parseInt(row[8]) || 0,
        currScore: parseFloat(row[9]) || 0, // Current attendance score
      };
      
      if (member.memberName) {
        members.push(member);
      }
    } catch (error) {
      console.warn(`Error parsing row ${i + 1}:`, error);
    }
  }
  
  return members;
}
